import React from 'react';
import { ProjectUnlockData } from '@/types/project';
import { formatCompactNumber, formatCompactCurrency } from '@/utils';
import UnlockPieChart from './UnlockPieChart';
import UnlockTimelineChart from './UnlockTimelineChart';
import { 
  Calendar, 
  DollarSign, 
  Lock, 
  Unlock,
  FileText
} from 'lucide-react';

interface ProjectUnlocksProps {
  unlockData: ProjectUnlockData | null;
}

const ProjectUnlocks: React.FC<ProjectUnlocksProps> = ({ unlockData }) => {
  if (!unlockData) {
    return (
      <div className="text-center py-12">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No unlock data available
        </p>
      </div>
    );
  }

  // Calculate unlock progress based on time elapsed in vesting period
  const calculateUnlockProgress = (allocation: { tge_unlock_percent?: number }) => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();
    const tgePercent = allocation.tge_unlock_percent || 0;
    
    if (now < vestingStart) return Math.min(tgePercent, 100);
    if (now >= vestingEnd) return 100;
    
    const totalVestingTime = vestingEnd - vestingStart;
    const elapsedTime = now - vestingStart;
    const timeBasedProgress = (elapsedTime / totalVestingTime) * (100 - tgePercent);
    
    return Math.min(Math.max(tgePercent + timeBasedProgress, 0), 100);
  };

  // Calculate timeline progress
  const calculateTimelineProgress = () => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();
    
    if (now < vestingStart) return 0;
    if (now >= vestingEnd) return 100;
    
    return Math.min(Math.max(((now - vestingStart) / (vestingEnd - vestingStart)) * 100, 0), 100);
  };

  const timelineProgress = calculateTimelineProgress();

  // Format date to DD/MM/YYYY
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  // Calculate unlocked and locked percentages
  const totalSupply = unlockData.total_supply || 1;
  const unlockedPercent = ((unlockData.total_unlocked || 0) / totalSupply * 100).toFixed(1);
  const lockedPercent = ((unlockData.total_locked || 0) / totalSupply * 100).toFixed(1);

  const renderCurrencyWithMutedDollar = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('$')) {
      return trimmedValue;
    }

    return (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {trimmedValue.slice(1)}
      </>
    );
  };

  return (
    <div className="space-y-7">
      {/* Vesting Timeline Bar */}
      <div className="bg-white dark:bg-[#1c1c1c] h-[140px] px-8 pt-8 pb-[31px]">
        {/* Dates */}
        <div className="flex justify-between">
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_start_date)}
          </span>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_end_date)}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="relative mt-4 h-[11px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)] rounded-[8px]">
          <div
            className="absolute h-[11px] bg-[#2d2923] dark:bg-[#fdfdfd] rounded-[8px] transition-all duration-300"
            style={{ width: `${timelineProgress}%` }}
          />
          {/* Current marker */}
          <div
            className="absolute top-0 h-[11px] w-[6px] bg-[#2d2923] dark:bg-[#fdfdfd] rounded-[8px]"
            style={{ left: `calc(${timelineProgress}% - 3px)` }}
          />
        </div>
        
        {/* Labels */}
        <div className="relative mt-3 h-[18px]">
          <span className="absolute left-0 font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting Start
          </span>
          <span className="absolute left-1/2 -translate-x-1/2 font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Current
          </span>
          <span className="absolute right-0 font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting End
          </span>
        </div>
      </div>

      {/* Token Distribution & Unlock Timeline */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Token Distribution */}
        <div className="h-[465px] bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] px-8 py-8">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
            Token Distribution
          </h2>

          <div className="mt-[49px] flex flex-col gap-6 xl:flex-row xl:items-start xl:gap-[116px] xl:pl-4">
            <div className="h-[298px] w-[298px] shrink-0">
              <UnlockPieChart unlockData={unlockData} />
            </div>

            <div className="flex w-full max-w-[278px] flex-col gap-[18px]">
              {/* Unlocked Card */}
              <div className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] p-5">
                <div className="mb-[18px] flex items-center gap-3">
                  <div className="w-[19px] h-[8px] bg-[#c4e804]" />
                  <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                    Unlocked
                  </span>
                </div>
                <p className="font-['Plus_Jakarta_Sans'] font-bold text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                  {formatCompactNumber(unlockData.total_unlocked)}
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {unlockedPercent}% of supply
                </p>
              </div>
              
              {/* Locked Card */}
              <div className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] p-5">
                <div className="mb-[18px] flex items-center gap-3">
                  <div className="w-[19px] h-[8px] bg-[#6b6969]" />
                  <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                    Locked
                  </span>
                </div>
                <p className="font-['Plus_Jakarta_Sans'] font-bold text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
                  {formatCompactNumber(unlockData.total_locked)}
                </p>
                <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {lockedPercent}% of supply
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Unlock Timeline */}
        <div className="h-[465px] bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] px-8 py-8">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] mb-6">
            Unlock Timeline
          </h2>
          
          <div className="mt-[67px] flex flex-col gap-4 xl:flex-row xl:items-start xl:gap-7">
            {/* Chart */}
            <div className="h-[305px] w-full xl:w-[468px] shrink-0">
              <UnlockTimelineChart unlockData={unlockData} />
            </div>
            
            {/* Date Cards */}
            <div className="flex w-full max-w-[216px] flex-col gap-3">
              <div className="h-[86px] bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-5 py-4">
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Vesting Start
                </span>
                <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mt-3">
                  {formatDate(unlockData.vesting_start_date)}
                </p>
              </div>
              <div className="h-[86px] bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-5 py-4">
                <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Vesting End
                </span>
                <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] mt-3">
                  {formatDate(unlockData.vesting_end_date)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,922px)_185px_185px_252px] gap-4">
        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-[56px] bg-white dark:bg-[#1c1c1c] px-4 py-[14px] flex items-center justify-between">
              <div className="flex items-center gap-[6px]">
                <DollarSign size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Total Supply
                </span>
              </div>
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {renderCurrencyWithMutedDollar(formatCompactCurrency(unlockData.total_supply))}
              </span>
            </div>

            <div className="h-[56px] bg-white dark:bg-[#1c1c1c] px-4 py-[14px] flex items-center justify-between">
              <div className="flex items-center gap-[6px]">
                <FileText size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Circulating Supply
                </span>
              </div>
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {renderCurrencyWithMutedDollar(formatCompactCurrency(unlockData.circulating_supply))}
              </span>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-[56px] bg-white dark:bg-[#1c1c1c] px-4 py-[14px] flex items-center justify-between">
              <div className="flex items-center gap-[6px]">
                <Lock size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Total Locked
                </span>
              </div>
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {renderCurrencyWithMutedDollar(formatCompactCurrency(unlockData.total_locked))}
              </span>
            </div>

            <div className="h-[56px] bg-white dark:bg-[#1c1c1c] px-4 py-[14px] flex items-center justify-between">
              <div className="flex items-center gap-[6px]">
                <Unlock size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
                <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Total Supply
                </span>
              </div>
              <span className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {renderCurrencyWithMutedDollar(formatCompactCurrency(unlockData.total_unlocked))}
              </span>
            </div>
          </div>
        </div>

        <div className="h-[128px] bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(23,23,23,0.4)] px-5 py-6 flex flex-col justify-between">
          <div className="flex items-center gap-[6px]">
            <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Vesting Start
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_start_date)}
          </span>
        </div>

        <div className="h-[128px] bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(23,23,23,0.4)] px-5 py-6 flex flex-col justify-between">
          <div className="flex items-center gap-[6px]">
            <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Vesting End
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_end_date)}
          </span>
        </div>

        <div className="h-[128px] bg-[rgba(255,255,255,0.4)] dark:bg-[rgba(23,23,23,0.4)] px-5 py-6 flex flex-col justify-between">
          <div className="flex items-center gap-[6px]">
            <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[13px] leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
              Next Unlock
            </span>
          </div>
          <div>
            {unlockData.next_unlock_tokens > 0 && (
              <p className="font-['Plus_Jakarta_Sans'] font-medium text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                {formatCompactNumber(unlockData.next_unlock_tokens)} tokens
              </p>
            )}
            <span className="font-['Plus_Jakarta_Sans'] font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {unlockData.next_unlock_date ? formatDate(unlockData.next_unlock_date) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Allocations Grid */}
      {unlockData.allocations && unlockData.allocations.length > 0 && (
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(23,23,23,0.32)] border border-white dark:border-[#171717] p-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {unlockData.allocations.map((allocation, index) => {
            const unlockProgress = calculateUnlockProgress(allocation);
            const details = [
              allocation.tge_unlock > 0
                ? { label: 'TGE Unlock', value: formatCompactNumber(allocation.tge_unlock) }
                : null,
              allocation.next_unlock_date
                ? { label: 'Next Unlock Date', value: formatDate(allocation.next_unlock_date) }
                : null,
              allocation.next_unlock_tokens > 0
                ? { label: 'Next Unlock Amount', value: formatCompactNumber(allocation.next_unlock_tokens) }
                : null,
            ].filter(Boolean) as Array<{ label: string; value: string }>;
            
            return (
              <div 
                key={index} 
                className="h-[219px] bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-6 py-5 flex flex-col"
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                      {allocation.name}
                    </h3>
                    <div className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      <span>{allocation.unlock_type.toLowerCase()}</span>
                      {allocation.tge_unlock_percent > 0 && (
                        <>
                          <span>{' • '}</span>
                          <span>{allocation.tge_unlock_percent}% TGE</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                      {renderCurrencyWithMutedDollar(formatCompactCurrency(allocation.tokens))}
                    </p>
                    <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      {allocation.allocation_of_supply.toFixed(1)}% of supply
                    </p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mt-[10px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                      Unlock Progress
                    </span>
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      {unlockProgress.toFixed(1)}% unlocked
                    </span>
                  </div>
                  <div className="h-[11px] rounded-[8px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)] overflow-hidden">
                    <div
                      className="h-[11px] rounded-[8px] bg-[#2d2923] dark:bg-[#fdfdfd] transition-all duration-300"
                      style={{ width: `${unlockProgress}%` }}
                    />
                  </div>
                </div>

                {/* Details */}
                <div
                  className="mt-4 grid gap-4"
                  style={{ gridTemplateColumns: `repeat(${Math.max(details.length, 1)}, minmax(0, 1fr))` }}
                >
                  {details.map((detail) => (
                    <div key={detail.label}>
                      <p className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] mb-1">
                        {detail.label}
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {detail.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectUnlocks; 
